import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import {
  Autocomplete,
  AutocompleteItem,
  Button,
  Card,
  CardBody,
  Chip,
  Input,
  Listbox,
  ListboxItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs,
  Tooltip,
} from '@nextui-org/react';
import { getAllSpecies } from 'common/src/api/species';
import {
  getIndustryClientMatches,
  getIndustryExporterGroups,
  getIndustryUnmatched,
  getMarketOptions,
  getShipOptions,
  refreshIndustryVolumeEta,
  setIndustryVolumeAlias,
  type IndustryClientMatch,
  type IndustryExporterGroup,
  type IndustryUnmatchedValue,
} from 'common/src/api/industryVolume';
import type { Species } from 'common/src/utils/types';
import Header from '../../../components/layouts/Header';
import {
  EmptyState,
  LoadingState,
  PageLayout,
  StatusChip,
} from '../../../components/ui';

const millionKilos = (kilos: number | null) =>
  ((kilos ?? 0) / 1e6).toLocaleString('es-CL', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

type ClientRow = {
  id: number;
  name: string;
  links: IndustryClientMatch[];
  suggestions: IndustryClientMatch[];
};

type ClientFilter = 'pending' | 'linked' | 'none' | 'all';

const statusOf = (row: ClientRow): Exclude<ClientFilter, 'all'> =>
  row.suggestions.length ? 'pending' : row.links.length ? 'linked' : 'none';

// Busca un nombre de la base para vincularlo a un cliente. La lista va dentro
// del modal (no en un popover) para que funcione igual en cualquier pantalla.
function AddNameModal({
  client,
  groups,
  onClose,
  onPick,
}: {
  client: ClientRow;
  groups: IndustryExporterGroup[];
  onClose: () => void;
  onPick: (key: string) => void;
}) {
  const [search, setSearch] = useState('');
  const query = search.trim().toUpperCase();
  const items = groups
    .filter(
      (g) =>
        g.exporter_id == null &&
        (!query || g.names.some((n) => n.toUpperCase().includes(query)))
    )
    .slice(0, 50);
  return (
    <Modal isOpen onClose={onClose} size='lg'>
      <ModalContent>
        <ModalHeader>Agregar nombre de la base a {client.name}</ModalHeader>
        <ModalBody>
          <Input
            label='Buscar en la base'
            value={search}
            onValueChange={setSearch}
            isClearable
            onClear={() => setSearch('')}
            startContent={<Icon icon='tabler:search' className='text-default-400' width={16} />}
          />
          <div className='max-h-80 overflow-y-auto rounded-medium border border-default-200'>
            <Listbox
              aria-label='Exportadoras de la base'
              items={items}
              onAction={(key) => onPick(String(key))}
              emptyContent='Sin resultados'
            >
              {(g) => (
                <ListboxItem
                  key={g.exporter_key}
                  description={`${millionKilos(g.kilos)} M kg · hasta ${g.last_season}`}
                >
                  {g.names.join(' / ')}
                </ListboxItem>
              )}
            </Listbox>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant='light' onPress={onClose}>
            Cancelar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function ClientsTab({ speciesId }: { speciesId: number }) {
  const [matches, setMatches] = useState<IndustryClientMatch[] | null>(null);
  const [groups, setGroups] = useState<IndustryExporterGroup[]>([]);
  const [filter, setFilter] = useState<ClientFilter>('pending');
  const [adding, setAdding] = useState<ClientRow | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const [m, g] = await Promise.all([
        getIndustryClientMatches(speciesId),
        getIndustryExporterGroups(speciesId),
      ]);
      setMatches(m);
      setGroups(g);
    } catch (error) {
      toast.error((error as Error).message);
      setMatches([]);
    }
  }, [speciesId]);

  useEffect(() => {
    setMatches(null);
    load();
  }, [load]);

  const rows = useMemo(() => {
    const byClient = new Map<number, ClientRow>();
    for (const m of matches ?? []) {
      const row =
        byClient.get(m.exporter_id) ||
        { id: m.exporter_id, name: m.exporter_name, links: [], suggestions: [] };
      if (m.status === 'linked') row.links.push(m);
      if (m.status === 'suggested') row.suggestions.push(m);
      byClient.set(m.exporter_id, row);
    }
    return Array.from(byClient.values());
  }, [matches]);

  const counts = useMemo(() => {
    const c = { pending: 0, linked: 0, none: 0, all: rows.length };
    rows.forEach((r) => (c[statusOf(r)] += 1));
    return c;
  }, [rows]);
  const visible = rows.filter((r) => filter === 'all' || statusOf(r) === filter);

  const save = async (client: number, ops: Parameters<typeof setIndustryVolumeAlias>[0][]) => {
    setBusy(client);
    try {
      for (const op of ops) await setIndustryVolumeAlias(op);
      await load();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setBusy(null);
    }
  };

  if (!matches) return <LoadingState label='Cargando exportadoras…' />;

  return (
    <div className='flex flex-col gap-4'>
      <Tabs
        size='sm'
        aria-label='Filtrar clientes'
        selectedKey={filter}
        onSelectionChange={(key) => setFilter(key as ClientFilter)}
      >
        <Tab key='pending' title={`Por confirmar (${counts.pending})`} />
        <Tab key='linked' title={`Vinculadas (${counts.linked})`} />
        <Tab key='none' title={`Sin nombres en la base (${counts.none})`} />
        <Tab key='all' title={`Todas (${counts.all})`} />
      </Tabs>
      <Card shadow='sm'>
        <CardBody className='p-6'>
          <Table aria-label='Exportadoras clientes' removeWrapper isStriped isCompact>
            <TableHeader>
              <TableColumn>EXPORTADORA</TableColumn>
              <TableColumn>NOMBRES EN LA BASE</TableColumn>
              <TableColumn>ESTADO</TableColumn>
              <TableColumn className='text-right'>ACCIONES</TableColumn>
            </TableHeader>
            <TableBody emptyContent={<EmptyState title='No hay exportadoras en este filtro.' />}>
              {visible.map((row) => {
                const status = statusOf(row);
                return (
                  <TableRow key={row.id}>
                    <TableCell className='font-medium'>{row.name}</TableCell>
                    <TableCell>
                      <div className='flex flex-wrap gap-1'>
                        {row.links.map((m) => (
                          <Chip
                            key={m.exporter_key}
                            size='sm'
                            variant='flat'
                            onClose={() =>
                              save(row.id, [
                                { speciesId, kind: 'exporter', raw: m.exporter_key!, remove: true },
                              ])
                            }
                          >
                            {(m.names ?? []).join(' / ')} · {millionKilos(m.kilos)} M kg
                          </Chip>
                        ))}
                        {row.suggestions.map((m) => (
                          <Tooltip key={m.exporter_key} content='Sugerido: confírmalo o descártalo'>
                            <Chip
                              size='sm'
                              variant='bordered'
                              onClose={() =>
                                // Descartar: queda como "no es de ningún cliente".
                                save(row.id, [
                                  { speciesId, kind: 'exporter', raw: m.exporter_key!, exporterId: null },
                                ])
                              }
                            >
                              {(m.names ?? []).join(' / ')} · {millionKilos(m.kilos)} M kg
                            </Chip>
                          </Tooltip>
                        ))}
                        {!row.links.length && !row.suggestions.length && (
                          <span className='text-sm text-default-400'>—</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {status === 'pending' ? (
                        <StatusChip tone='warning'>Por confirmar</StatusChip>
                      ) : status === 'linked' ? (
                        <StatusChip tone='success'>Vinculada</StatusChip>
                      ) : (
                        <StatusChip>Sin nombres</StatusChip>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className='flex justify-end gap-2'>
                        {row.suggestions.length > 0 && (
                          <Button
                            size='sm'
                            variant='flat'
                            isLoading={busy === row.id}
                            onPress={() =>
                              save(
                                row.id,
                                row.suggestions.map((m) => ({
                                  speciesId,
                                  kind: 'exporter' as const,
                                  raw: m.exporter_key!,
                                  exporterId: row.id,
                                }))
                              )
                            }
                          >
                            Confirmar
                          </Button>
                        )}
                        <Tooltip content='Agregar un nombre de la base'>
                          <Button
                            isIconOnly
                            size='sm'
                            variant='light'
                            aria-label='Agregar un nombre de la base'
                            onPress={() => setAdding(row)}
                          >
                            <Icon icon='tabler:plus' width={16} />
                          </Button>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardBody>
      </Card>
      {adding && (
        <AddNameModal
          client={adding}
          groups={groups}
          onClose={() => setAdding(null)}
          onPick={(key) => {
            const client = adding;
            setAdding(null);
            save(client.id, [{ speciesId, kind: 'exporter', raw: key, exporterId: client.id }]);
          }}
        />
      )}
    </div>
  );
}

type Option = { key: string; label: string; description?: string };

const MAX_UNMATCHED_ROWS = 100;

function UnmatchedTab({
  speciesId,
  kind,
  options,
}: {
  speciesId: number;
  kind: 'port' | 'ship';
  options: Option[];
}) {
  const [rows, setRows] = useState<IndustryUnmatchedValue[] | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      setRows(await getIndustryUnmatched(speciesId, kind));
    } catch (error) {
      toast.error((error as Error).message);
      setRows([]);
    }
  }, [speciesId, kind]);

  useEffect(() => {
    setRows(null);
    load();
  }, [load]);

  const assign = async (raw: string, key: string) => {
    setSaving(raw);
    try {
      await setIndustryVolumeAlias({
        speciesId,
        kind,
        raw,
        ...(kind === 'port' ? { marketId: key } : { shipId: Number(key) }),
      });
      toast.success(`"${raw}" quedó asignado`);
      await load();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSaving(null);
    }
  };

  if (!rows) return <LoadingState label='Cargando…' />;

  // Pueden ser cientos: se buscan y se muestran los que más pesan.
  const query = search.trim().toUpperCase();
  const filtered = rows.filter(
    (r) => !query || r.raw.toUpperCase().includes(query) || (r.detail ?? '').toUpperCase().includes(query)
  );
  const visible = filtered.slice(0, MAX_UNMATCHED_ROWS);

  return (
    <Card shadow='sm'>
      <CardBody className='gap-4 p-6'>
        <div className='flex flex-wrap items-center gap-3'>
          <Input
            className='max-w-xs'
            label={kind === 'port' ? 'Buscar puerto o país' : 'Buscar nave o puerto'}
            value={search}
            onValueChange={setSearch}
            isClearable
            onClear={() => setSearch('')}
          />
          <span className='text-sm text-default-500'>
            {filtered.length > visible.length
              ? `${visible.length} de ${filtered.length}, de los que más pesan`
              : `${filtered.length} sin calzar`}
          </span>
        </div>
        <Table
          aria-label={kind === 'port' ? 'Puertos sin calzar' : 'Naves sin calzar'}
          removeWrapper
          isStriped
          isCompact
        >
          <TableHeader>
            <TableColumn>{kind === 'port' ? 'PUERTO EN LA BASE' : 'NAVE EN LA BASE'}</TableColumn>
            <TableColumn>{kind === 'port' ? 'PAÍS' : 'PUERTO DE DESTINO'}</TableColumn>
            <TableColumn className='text-right'>M KG</TableColumn>
            <TableColumn>ÚLTIMA TEMPORADA</TableColumn>
            <TableColumn>{kind === 'port' ? 'MARKET' : 'NAVE'}</TableColumn>
          </TableHeader>
          <TableBody
            emptyContent={
              <EmptyState
                icon='tabler:circle-check'
                title={kind === 'port' ? 'Todos los puertos calzan.' : 'Todas las naves calzan.'}
              />
            }
          >
            {visible.map((row) => (
              <TableRow key={row.raw}>
                <TableCell className='font-medium'>{row.raw}</TableCell>
                <TableCell>{row.detail ?? '—'}</TableCell>
                <TableCell className='text-right'>{millionKilos(row.kilos)}</TableCell>
                <TableCell className='whitespace-nowrap'>{row.last_season}</TableCell>
                <TableCell>
                  <Autocomplete
                    aria-label={`Asignar ${row.raw}`}
                    size='sm'
                    placeholder='Buscar…'
                    className='min-w-[240px]'
                    defaultItems={options}
                    isDisabled={saving === row.raw}
                    onSelectionChange={(key) => key && assign(row.raw, String(key))}
                  >
                    {(o) => (
                      <AutocompleteItem key={o.key} description={o.description}>
                        {o.label}
                      </AutocompleteItem>
                    )}
                  </Autocomplete>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardBody>
    </Card>
  );
}

/**
 * Equivalencias de la base de volumen de industria: qué nombres de la base son
 * de cada cliente nuestro, y los puertos y naves que no calzaron solos.
 */
const SuperadminVolume = () => {
  const [species, setSpecies] = useState<Species[]>([]);
  const [speciesId, setSpeciesId] = useState<number | null>(null);
  const [tab, setTab] = useState('clients');
  const [markets, setMarkets] = useState<Option[]>([]);
  const [ships, setShips] = useState<Option[]>([]);
  const [refreshingEta, setRefreshingEta] = useState(false);

  // El tracking de las naves cambia durante la temporada: los arribos de la
  // última temporada se recalculan a mano (también al subir la base).
  const refreshEta = async () => {
    if (!speciesId) return;
    setRefreshingEta(true);
    try {
      const result = await refreshIndustryVolumeEta(speciesId);
      const bySource = Object.entries(result.by_source ?? {})
        .map(([source, kilos]) => `${source}: ${millionKilos(kilos)} M kg`)
        .join(' · ');
      toast.success(`Arribos de ${result.season} actualizados (${bySource}).`, { autoClose: 8000 });
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setRefreshingEta(false);
    }
  };

  useEffect(() => {
    getAllSpecies()
      .then((list) => {
        setSpecies(list);
        const cherry = list.find((s) => s.excel_name?.toUpperCase() === 'CHERRY');
        setSpeciesId((current) => current ?? cherry?.id ?? list[0]?.id ?? null);
      })
      .catch(() => setSpecies([]));
    getMarketOptions()
      .then((list) =>
        setMarkets(
          list.map((m) => ({ key: m.id, label: m.name, description: `${m.id} · ${m.country ?? ''}` }))
        )
      )
      .catch(() => setMarkets([]));
    getShipOptions()
      .then((list) => setShips(list.map((s) => ({ key: String(s.id), label: s.name }))))
      .catch(() => setShips([]));
  }, []);

  return (
    <PageLayout>
      <Header
        title='Volumen industria'
        description='Equivalencias entre la base de volumen y nuestros catálogos. La base se sube en Documentos.'
        actions={
          <Button
            variant='flat'
            startContent={<Icon icon='tabler:refresh' width={16} />}
            isLoading={refreshingEta}
            isDisabled={!speciesId}
            onPress={refreshEta}
          >
            Actualizar arribos
          </Button>
        }
      />
      <div className='mb-6 max-w-xs'>
        <Select
          label='Especie'
          selectedKeys={speciesId ? [String(speciesId)] : []}
          disallowEmptySelection
          onSelectionChange={(keys) => {
            const key = Array.from(keys)[0];
            if (key) setSpeciesId(Number(key));
          }}
        >
          {species.map((s) => (
            <SelectItem key={String(s.id)}>{s.name}</SelectItem>
          ))}
        </Select>
      </div>
      {!speciesId ? (
        <LoadingState label='Cargando…' />
      ) : (
        <Tabs
          aria-label='Equivalencias'
          variant='underlined'
          selectedKey={tab}
          onSelectionChange={(key) => setTab(String(key))}
        >
          <Tab key='clients' title='Clientes'>
            <ClientsTab speciesId={speciesId} />
          </Tab>
          <Tab key='ports' title='Puertos sin calzar'>
            <UnmatchedTab speciesId={speciesId} kind='port' options={markets} />
          </Tab>
          <Tab key='ships' title='Naves sin calzar'>
            <UnmatchedTab speciesId={speciesId} kind='ship' options={ships} />
          </Tab>
        </Tabs>
      )}
    </PageLayout>
  );
};

export default SuperadminVolume;
